import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getMySchedule, setMyAvailability } from '@/api/TournamentManagement/refereeApi';
import { RefereeAvailability } from '@/models/tournament';
import type { RefereeScheduleView } from '@/models/tournament';
import ScheduleCalendar from '@/components/tournament/ScheduleCalendar';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import Icon from '@/components/AppIcon';

const MyScheduleTab: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { toast, showToast, dismissToast } = useToast();

  const [entries, setEntries] = useState<RefereeScheduleView[]>([]);
  // Start as true — spinner shows immediately before ScheduleCalendar's
  // onRangeChange fires on mount and triggers the first fetch.
  const [calendarLoading, setCalendarLoading] = useState(true);
  // Set when getMySchedule returns 404 (no referee profile).
  const [noProfile, setNoProfile] = useState(false);

  const handleRangeChange = useCallback(async (start: string, end: string) => {
    setCalendarLoading(true);
    try {
      const result = await getMySchedule(start, end);
      if (result.errors.length === 0 && result.payload) {
        setEntries(result.payload);
        setNoProfile(false);
      } else {
        setNoProfile(result.status === 404);
      }
    } catch (err: any) {
      if (err?.status === 404) {
        setNoProfile(true);
      }
    }
    finally { setCalendarLoading(false); }
  }, []);

  const handleSaveAvailability = async (changes: { date: string; status: RefereeAvailability }[]) => {
    try {
      const result = await setMyAvailability(changes);
      if (result.errors.length === 0 && result.payload) {
        setEntries(result.payload);
        showToast(t('Availability updated successfully'), 'success');
      } else {
        showToast(result.errors[0]?.description || t('Failed to update availability'), 'error');
        throw new Error('save failed');
      }
    } catch (err: any) {
      if (err?.status === 404) {
        setNoProfile(true);
      } else if (err?.errors?.[0]?.description) {
        showToast(err.errors[0].description, 'error');
      }
      throw new Error('save failed');
    }
  };

  if (noProfile) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="no-profile-schedule">
        <Icon name="UserX" size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('You do not have a referee profile')}</h3>
        <p className="text-muted-foreground">{t('Contact an administrator to create your referee profile.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-schedule-tab">
      <ScheduleCalendar
        entries={entries}
        loading={calendarLoading}
        readOnly={false}
        onRangeChange={handleRangeChange}
        onSaveAvailability={handleSaveAvailability}
      />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default MyScheduleTab;
